import React, { Fragment, useEffect, useContext, useState } from 'react';
import { Button, Dialog, Box } from '@mui/material';
import { connect } from 'react-redux';
import { Form } from 'react-final-form';
import moment from 'moment';
import {
  kycGotoPrevPage as kycGotoPrevPageRoutine,
  kycGotoNextPage as kycGotoNextPageRoutine,
  kycSetDraftBeneficialOwners,
  kycSubmitCustomerDetails as kycSubmitCustomerDetailsRoutine,
} from 'spa/actions/VerificationActions';
import VerificationConstants, {
  KYCPages,
  UBO_NOT_PUBLICLY_LISTED_VALUE,
} from 'spa/constants/VerificationConstants';
import ActionButtons from 'spa/components/CustomerVerification/V2/ActionButtons';
import SubmitButton from 'spa/components/StartTransaction/SubmitButton';
import {
  customerKycHasPrevPagesSelector,
  customerKycDraftHasUBOSelector,
  customerKycDraftStockExchangesSelector,
  customerKycDraftCompanyDetailsSelector,
  customerKycDraftBeneficialOwnersSelector,
  customerKycSubmitLoadingSelector,
  customerUboDetailsSelector,
} from 'spa/selectors/CustomerSelectors';
import { KYCContext } from 'spa/context/KYCContext';
import { Alpha3ToAlpha2, Alpha2ToCountry } from 'spa/constants/ISOCountryCodes';
import Heading from './Heading';
import IndividualDetailsFields from './IndividualDetailsFields';
import useFullWidthKycDetails from './useFullWidthKycDetails';

const getOwnerDisplayName = (draftOwner) => {
  const values = (draftOwner && draftOwner.values) || {};
  const name = [values['first-name'], values['middle-name'], values['last-name']]
    .filter(Boolean)
    .join(' ');
  return name || 'Beneficial Owner';
};

const serializeOwnerValues = (values = {}) => {
  const dob = values['date-of-birth'];
  if (!dob || typeof dob === 'string') {
    return values;
  }
  const parsed = moment.isMoment(dob) ? dob : moment(dob);
  return {
    ...values,
    'date-of-birth': parsed.isValid() ? parsed.format('YYYY-MM-DD') : dob,
  };
};

const getOwnerDateOfBirth = (draftOwner) => {
  const dob = draftOwner && draftOwner.values && draftOwner.values['date-of-birth'];
  if (!dob) {
    return null;
  }
  const parsed = moment.isMoment(dob) ? dob : moment(dob);
  return parsed.isValid() ? parsed.format('D / MMM / YYYY') : null;
};

const getOwnerAddressDisplay = (draftOwner) => {
  const values = (draftOwner && draftOwner.values) || {};
  return [values.address, values.city, values.state, values['full-country'] || values.country]
    .filter(Boolean)
    .join(' ');
};

const getOwnerKey = (draftOwner, index) => {
  const values = (draftOwner && draftOwner.values) || {};
  if (values.id != null) {
    return `ubo-${values.id}`;
  }
  return `new-ubo-${index}`;
};

const buildOwnerCompleteAddress = (address) =>
  [address.line1, address.city, address.state, address.post_code].filter(Boolean).join(', ');

const mapUboDetailToOwnerValues = (uboDetail) => {
  const address = uboDetail.address || {};
  return {
    id: uboDetail.id,
    'first-name': uboDetail.first_name,
    'last-name': uboDetail.last_name,
    'date-of-birth': uboDetail.date_of_birth
      ? moment(uboDetail.date_of_birth).format('YYYY-MM-DD')
      : undefined,
    address: address.line1,
    city: address.city,
    state: address.state,
    'post-code': address.post_code,
    country: address.country,
    'full-country': Alpha2ToCountry[address.country],
    'complete-address': buildOwnerCompleteAddress(address),
  };
};

const mapUboDetailsToOwners = (uboDetails) =>
  uboDetails.map((uboDetail) => {
    const values = mapUboDetailToOwnerValues(uboDetail);
    return { values, serverValues: values };
  });

const getUboDetailPayload = (draftOwner) => {
  const { values, serverValues, deleted } = draftOwner;
  if (deleted) {
    return { id: values.id };
  }
  if (!serverValues) {
    const { id, ...newOwnerFields } = values;
    return newOwnerFields;
  }
  const hasChanges = Object.keys({ ...serverValues, ...values }).some(
    (key) => key !== 'id' && values[key] !== serverValues[key]
  );
  return hasChanges ? { ...values } : null;
};

const BeneficialOwnersForm = ({
  draftCompanyDetails,
  draftHasUBO,
  draftStockExchanges,
  draftBeneficialOwners,
  existingUboDetails,
  isSubmitting,
  hasPrevPages,
  loadPrevPage,
  gotoNextPage,
  setDraftBeneficialOwners,
  submitIndividualDetails,
}) => {
  const kycContext = useContext(KYCContext);
  const { forceCountry } = kycContext;
    const { fullWidth, fullWidthClassName } = useFullWidthKycDetails();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOwnerIndex, setEditingOwnerIndex] = useState(null);
  const [showAutocompleteAddressError, setShowAutocompleteAddressError] = useState(false);
  const [deletingOwnerIndex, setDeletingOwnerIndex] = useState(null);

  const beneficialOwners = draftBeneficialOwners || mapUboDetailsToOwners(existingUboDetails);
  const visibleBeneficialOwners = beneficialOwners.filter((draftOwner) => !draftOwner.deleted);
  const isEditing = editingOwnerIndex !== null;

  const defaultCountry =
    forceCountry ||
    (Alpha3ToAlpha2[window.config.geoip_country] &&
      Alpha3ToAlpha2[window.config.geoip_country].toLowerCase()) ||
    'us';

  useEffect(() => {
    if (!draftCompanyDetails) {
      gotoNextPage({
        nextPage: KYCPages.COMPANY_DETAILS_FORM,
        forceNextPage: true,
        purgeHistory: true,
        kycContext,
      });
    } else if (!draftHasUBO) {
      gotoNextPage({
        nextPage: KYCPages.HAS_UBO_FORM,
        forceNextPage: true,
        purgeHistory: true,
        kycContext,
      });
    } else if (!draftStockExchanges) {
      gotoNextPage({
        nextPage: KYCPages.STOCK_EXCHANGE_FORM,
        forceNextPage: true,
        purgeHistory: true,
        kycContext,
      });
    }
  }, [
    draftCompanyDetails,
    draftHasUBO,
    draftStockExchanges,
    gotoNextPage,
    kycContext,
  ]);

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingOwnerIndex(null);
    setShowAutocompleteAddressError(false);
  };

  const openAddModal = () => {
    setEditingOwnerIndex(null);
    setIsModalOpen(true);
  };

  const openEditModal = (index) => {
    setEditingOwnerIndex(index);
    setIsModalOpen(true);
  };

  const saveBeneficialOwner = (draftOwner) => {
    const previousOwner = isEditing ? beneficialOwners[editingOwnerIndex] : null;
    const serializedOwner = {
      serverValues: previousOwner && previousOwner.serverValues,
      values: serializeOwnerValues(draftOwner.values),
    };
    if (isEditing) {
      const updatedOwners = [...beneficialOwners];
      updatedOwners[editingOwnerIndex] = serializedOwner;
      setDraftBeneficialOwners(updatedOwners);
    } else {
      setDraftBeneficialOwners([...beneficialOwners, serializedOwner]);
    }
    closeModal();
  };

  const deleteBeneficialOwner = (index) => {
    const draftOwner = beneficialOwners[index];
    const ownerId = draftOwner && draftOwner.values && draftOwner.values.id;
    const updatedOwners = [...beneficialOwners];
    if (ownerId) {
      updatedOwners[index] = { values: { id: ownerId }, deleted: true };
    } else {
      updatedOwners.splice(index, 1);
    }
    setDraftBeneficialOwners(updatedOwners);
  };

  const openDeleteModal = (index) => {
    setDeletingOwnerIndex(index);
  };

  const closeDeleteModal = () => {
    setDeletingOwnerIndex(null);
  };

  const confirmDeleteBeneficialOwner = () => {
    deleteBeneficialOwner(deletingOwnerIndex);
    closeDeleteModal();
  };

  const onFormSubmit = () => {
    const beneficialOwnerValues = beneficialOwners.map(getUboDetailPayload).filter(Boolean);
    const draftStockExchangeValues = (draftStockExchanges && draftStockExchanges.values) || {};
    const draftValues = {
      ...((draftCompanyDetails && draftCompanyDetails.values) || {}),
      ...((draftHasUBO && draftHasUBO.values) || {}),
      ...draftStockExchangeValues,
      stock_exchanges: (draftStockExchangeValues.stock_exchanges || []).filter(
        (value) => value !== UBO_NOT_PUBLICLY_LISTED_VALUE
      ),
      ubo_details: beneficialOwnerValues,
    };
    const draftRegisteredFields = [
      ...new Set([
        ...((draftCompanyDetails && draftCompanyDetails.registeredFields) || []),
        ...((draftHasUBO && draftHasUBO.registeredFields) || []),
        ...((draftStockExchanges && draftStockExchanges.registeredFields) || []),
        'ubo_details',
      ]),
    ];
    submitIndividualDetails({
      formName: VerificationConstants.BENEFICIAL_OWNERS_LIST_FORM,
      registeredFields: draftRegisteredFields,
      values: draftValues,
      kycContext,
    });
  };

  return (
    <div>
      <Heading text="Beneficial Owners" hasDivider />

      {beneficialOwners.map((draftOwner, index) => {
        if (draftOwner.deleted) {
          return null;
        }
        const dateOfBirth = getOwnerDateOfBirth(draftOwner);
        const addressDisplay = getOwnerAddressDisplay(draftOwner);
        return (
          <div
            key={getOwnerKey(draftOwner, index)}
            className={`kyc-details kyc-details--owner-row${fullWidthClassName}`}
          >
            <div className={`kyc-details-text${fullWidthClassName}`}>
              <Fragment>
                {getOwnerDisplayName(draftOwner)}
                {dateOfBirth && (
                  <Fragment>
                    <br />
                    {dateOfBirth}
                  </Fragment>
                )}
                {addressDisplay && (
                  <Fragment>
                    <br />
                    {addressDisplay}
                  </Fragment>
                )}
              </Fragment>
            </div>
            <div className={`kyc-details-buttons${fullWidthClassName}`}>
              <div className={`kyc-details-buttons-button${fullWidthClassName}`}>
                <SubmitButton
                  fullWidth={fullWidth}
                  onClick={() => openEditModal(index)}
                  muiVariant="outlined"
                >
                  Edit
                </SubmitButton>
              </div>
              <div className={`kyc-details-buttons-button${fullWidthClassName}`}>
                <SubmitButton
                  fullWidth={fullWidth}
                  onClick={() => openDeleteModal(index)}
                  muiVariant="outlined"
                  color="error"
                >
                  Delete
                </SubmitButton>
              </div>
            </div>
          </div>
        );
      })}

      <Button
        type="button"
        variant="outlined"
        color="primary"
        fullWidth={fullWidth}
        onClick={openAddModal}
        style={{ marginBottom: visibleBeneficialOwners.length === 0 ? undefined : '0.5rem' }}
      >
        + Add a beneficial owner
      </Button>

      <ActionButtons
        secondaryText="Back"
        primaryText="Save and Next"
        isLoading={isSubmitting}
        isDisabled={visibleBeneficialOwners.length === 0}
        type="button"
        withBack={hasPrevPages}
        onSecondaryClick={() => loadPrevPage()}
        onPrimaryClick={onFormSubmit}
      />

      <Dialog
        maxWidth="md"
        fullWidth
        open={isModalOpen}
        onClose={closeModal}
        PaperProps={{
          style: {
            margin: fullWidth ? 0 : undefined,
            padding: 0,
            overflow: 'auto',
          },
        }}
      >
        <Box className="kyc-modal-box">
          <div className="kyc-modal-box-title" style={{marginBottom: "1rem"}}>
            {isEditing ? 'Edit Beneficial Owner' : 'Add Beneficial Owner'}
          </div>
          {isModalOpen && (
            <Form
              onSubmit={(ownerValues) => {
                saveBeneficialOwner({ values: ownerValues });
              }}
              initialValues={
                isEditing && beneficialOwners[editingOwnerIndex]
                  ? beneficialOwners[editingOwnerIndex].values
                  : undefined
              }
              subscription={{
                values: true,
                hasValidationErrors: true,
              }}
              mutators={{
                clearAutocompleteAddressError: ([prefix], state) => {
                  state.fields[`${prefix}address`].data.error = undefined;
                  state.fields[`${prefix}city`].data.error = undefined;
                  state.fields[`${prefix}state`].data.error = undefined;
                },
              }}
            >
              {({
                handleSubmit: handleOwnerSubmit,
                hasValidationErrors,
                values: ownerValues,
              }) => (
                <form
                  onSubmit={handleOwnerSubmit}
                  name={VerificationConstants.BENEFICIAL_OWNER_FORM}
                >
                  <div className="kyc">
                    <div className="kyc-modal-box-body">
                      <IndividualDetailsFields
                        values={ownerValues}
                        defaultCountry={defaultCountry}
                        lockCountry={!!forceCountry}
                        showAutocompleteAddressError={showAutocompleteAddressError}
                        setShowAutocompleteAddressError={setShowAutocompleteAddressError}
                        showHeading={false}
                        showPhoneNumber={false}
                        showVat={false}
                        showOccupation={false}
                        showLocalName={false}
                      />
                    </div>
                    <div className="kyc-modal-box-buttons" style={{ gap: '10px' }}>
                      <Button variant="text" type="button" onClick={closeModal}>
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        type="submit"
                        disabled={hasValidationErrors}
                        onClick={() => setShowAutocompleteAddressError(true)}
                      >
                        {isEditing ? 'Edit' : 'Add'}
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </Form>
          )}
        </Box>
      </Dialog>

      <Dialog
        maxWidth="sm"
        fullWidth
        open={deletingOwnerIndex !== null}
        onClose={closeDeleteModal}
        PaperProps={{
          style: {
            margin: fullWidth ? 0 : undefined,
            padding: 0,
            overflow: 'auto',
          },
        }}
      >
        <Box className="kyc-modal-box">
          <div className="kyc-modal-box-title">
            Deleting UBO Information
          </div>
          <div>
            Are you sure you want to delete the UBO Information for{' '}
            {deletingOwnerIndex !== null &&
              getOwnerDisplayName(beneficialOwners[deletingOwnerIndex])}
            ?
          </div>
          <div className="kyc-modal-box-buttons" style={{ gap: '10px' }}>
            <Button variant="text" type="button" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              type="button"
              onClick={confirmDeleteBeneficialOwner}
            >
              Delete
            </Button>
          </div>
        </Box>
      </Dialog>
    </div>
  );
};

const mapStateToProps = (state) => ({
  draftCompanyDetails: customerKycDraftCompanyDetailsSelector(state),
  draftHasUBO: customerKycDraftHasUBOSelector(state),
  draftStockExchanges: customerKycDraftStockExchangesSelector(state),
  draftBeneficialOwners: customerKycDraftBeneficialOwnersSelector(state),
  existingUboDetails: customerUboDetailsSelector(state),
  isSubmitting: customerKycSubmitLoadingSelector(state),
  hasPrevPages: customerKycHasPrevPagesSelector(state),
});


const mapDispatchToProps = (dispatch) => ({
  loadPrevPage: () => dispatch(kycGotoPrevPageRoutine.trigger()),
  gotoNextPage: (payload) => dispatch(kycGotoNextPageRoutine.trigger(payload)),
  setDraftBeneficialOwners: (payload) => dispatch(kycSetDraftBeneficialOwners(payload)),
  submitIndividualDetails: (payload) => dispatch(kycSubmitCustomerDetailsRoutine.trigger(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BeneficialOwnersForm);
